<template>
  <div class="fish-grid" :style="{ '--colunas-telefone': colunasTelefone, '--colunas-desktop': colunasDesktop }">
    <slot />

    <!--
      A LINHA VAZIA DO FIM (D17) — a regra que faz a grade que cresce funcionar.

      > A grade cresce conforme entra item, em linhas de largura fixa — nunca uma grade de 8×5 que
      > acaba. **E sempre deixa uma linha vazia visível no fim.**

      É o que diz "cabe mais aqui" sem escrever nada, e impede a mochila de parecer cheia ou
      travada. Os encaixes fantasma são `aria-hidden`: eles falam com o olho, não com o leitor de
      tela, e anunciar oito "vazio" no fim de cada lista seria ruído puro.
    -->
    <div v-for="n in fantasmas" :key="`fantasma-${n}`" class="fish-grid__fantasma" aria-hidden="true" />
  </div>
</template>

<script>
/**
 * 2.6 — `FishGrid`: a grade de encaixes que **cresce** (D17).
 *
 * ## A largura, e por que ela é fixa por aparelho
 *
 * **4 encaixes no telefone, 8 no desktop.** Não é `auto-fill`: com `auto-fill` a contagem de colunas
 * muda a cada pixel de largura, e uma grade cuja quantidade de itens por linha muda enquanto se
 * rola é uma grade em que não dá para lembrar onde uma coisa estava. Número fixo por faixa mantém a
 * memória espacial, que é metade do que faz um inventário ser rápido de usar.
 *
 * ## Por que a linha vazia importa mais do que parece
 *
 * Uma grade que termina exatamente no último item lê como **cheia**. O jogador não tem como saber
 * se comprar mais uma isca vai caber, e a dúvida vira hesitação na loja. Uma fileira vazia responde
 * isso sem uma palavra — e é mais barato que qualquer texto de "espaço: 34/50", que ainda por cima
 * seria mentira, já que o inventário do Fishbay não tem teto.
 */
export default {
  name: 'FishGrid',
  props: {
    /** Quantos itens a grade contém. Só serve para calcular a linha vazia. */
    total: { type: Number, default: 0 },
    colunasTelefone: { type: Number, default: 4 },
    colunasDesktop: { type: Number, default: 8 },
    /** Desliga a linha vazia. Para a grade de uma loja, que não é um recipiente que enche. */
    semLinhaVazia: { type: Boolean, default: false },
  },
  data() {
    return { estreito: true };
  },
  computed: {
    colunas() {
      return this.estreito ? this.colunasTelefone : this.colunasDesktop;
    },
    /**
     * Quantos encaixes fantasma completam a linha atual **mais uma linha inteira**.
     *
     * O `% colunas` fecha a linha em que o último item parou; o `+ colunas` acrescenta a fileira
     * vazia. Quando o total é múltiplo exato das colunas, o resto é 0 e só a fileira nova entra —
     * que é o comportamento certo, e o motivo de o cálculo não ser `colunas - total % colunas`
     * (esse daria uma linha inteira a mais quando a última já estava fechada).
     */
    fantasmas() {
      if (this.semLinhaVazia) return 0;
      const sobra = this.total % this.colunas;
      return (sobra === 0 ? 0 : this.colunas - sobra) + this.colunas;
    },
  },
  mounted() {
    this.medir();
    window.addEventListener('resize', this.medir, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.medir);
  },
  methods: {
    medir() {
      // 900px é o breakpoint "desktop" dos quatro que sobraram (UI-08). O `matchMedia` é lido em JS
      // porque o número de fantasmas depende da contagem de colunas, e CSS não sabe contar.
      this.estreito = !window.matchMedia('(min-width: 900px)').matches;
    },
  },
};
</script>

<style scoped>
.fish-grid {
  display: grid;
  grid-template-columns: repeat(var(--colunas-telefone, 4), 1fr);
  gap: var(--e-2);
}

@media (min-width: 900px) {
  .fish-grid {
    grid-template-columns: repeat(var(--colunas-desktop, 8), 1fr);
  }
}

/**
 * O encaixe fantasma.
 *
 * Ele é MAIS APAGADO que um encaixe vazio de verdade, e a diferença é intencional: um encaixe vazio
 * é um lugar que existe e espera item; um fantasma é só a promessa de que a grade continua. Se os
 * dois tivessem o mesmo peso, a mochila pareceria ter trinta buracos em vez de doze itens.
 */
.fish-grid__fantasma {
  aspect-ratio: 1;
  min-height: var(--alvo-minimo);
  border: 2px dashed var(--borda);
  opacity: 0.28;
}
</style>
